import {
  addDisposableEventListener,
  queryElementOrThrow,
  UxplComponent,
} from '../core';

import { Listbox } from './listbox';

import { Attributes, OptionClasses, OptionEqualityFn } from './shared';

export interface SelectOption {
  text: string;
  value?: any;
  disabled?: boolean;
}

const strictEquals = (opt1: any, opt2: any) => opt1 === opt2;

export interface ComboboxConfig {
  /**
   * initially selected value
   * */
  selected?: SelectOption;
  /**
   * initial options
   */
  options?: SelectOption[];
  /**
   * Optinal message to show if there are no results to display.
   * If not provided the dropdown won't be displayed when results are empty.
   */
  emptyMessage?: string;
  /**
   * Do not reset the input box if the user doesn't select anything
   */
  keepUserInput?: boolean;
  /**
   * Function to be called to check if two given options are equal.
   * Defaults to strict equality `opt1 === opt2`
   */
  isOptionEqual?: OptionEqualityFn;
  /**
   * Enable/disable the outside click-to-close of combobox.
   */
  ignoreClickOutside?: boolean;
}

interface BaseComboboxEvent {
  combobox: Combobox;
}

export interface ComboboxEventMap {
  selected: BaseComboboxEvent & { selected: SelectOption };
  /**
   * triggered whenever the input value changes as a result of a user interaction
   */
  search: BaseComboboxEvent & { value: string };
  /**
   * triggered whenever the input value changes, wether by use interaction
   * or when something else changes it
   */
  inputChange: BaseComboboxEvent & { value: string };
  beforeOpen: BaseComboboxEvent;
  afterOpen: BaseComboboxEvent;
  beforeClose: BaseComboboxEvent;
  afterClose: BaseComboboxEvent;
  enabledChange: BaseComboboxEvent & { enabled: boolean };
  // fires after the first time user interacts with the element
  // useful for integrating with form validation frameworks
  touched: BaseComboboxEvent;
}

export class Combobox extends UxplComponent<ComboboxEventMap> {
  private comboboxRef: Element;
  private listbox: Listbox;
  private domRefs: {
    listRef: Element;
    inputRef: HTMLInputElement;
    dropdownRef: Element;
    togglerRef?: Element;
  };
  private touched = true;
  private keepUserInput?: boolean;
  private isOpen = false;
  private inputTextOnOpen = '';
  private hasSelectionChanged: boolean;
  private shouldCloseOnClickOutside: boolean;

  constructor(comboboxRef: Element, config?: ComboboxConfig) {
    super();
    if (!comboboxRef) {
      throw new Error(
        'Uxpl.Combobox could not be initialized. Reason: required root element not provided'
      );
    }
    this.comboboxRef = comboboxRef;
    config = config || {};

    this.shouldCloseOnClickOutside = !config.ignoreClickOutside;
    this.keepUserInput = config.keepUserInput;
    this.domRefs = {
      listRef: queryElementOrThrow({
        parent: this.comboboxRef,
        selector: `[${Attributes.Listbox}]`,
        throwIfNotFound: `Uxpl.Combobox could not be initialized because the listbox element was not found ([${Attributes.Listbox}])`,
      }),
      dropdownRef: queryElementOrThrow({
        parent: this.comboboxRef,
        selector: `[${Attributes.Dropdown}]`,
        throwIfNotFound: `Uxpl.Combobox could not be initialized because the dropdown element was not found ([${Attributes.Dropdown}])`,
      }),
      togglerRef: queryElementOrThrow({
        parent: this.comboboxRef,
        selector: `[${Attributes.Toggler}]`,
      }),
      inputRef: queryElementOrThrow({
        parent: this.comboboxRef,
        selector: `[${Attributes.Input}]`,
        throwIfNotFound: `Uxpl.Combobox could not be initialized because the input element was not found ([${Attributes.Input}])`,
      }) as HTMLInputElement,
    };
    this.listbox = new Listbox(this.domRefs.listRef, {
      options: config.options,
      selected: config.selected,
      emptyMessage: config.emptyMessage,
      isOptionEqual: config.isOptionEqual || strictEquals,
    });
    this.attachListeners();
    if (config.selected) {
      this.setSelected(config.selected);
    }
  }

  setSelected(option: SelectOption): void {
    this.domRefs.inputRef.value = option.text || '';
    this.listbox.setSelected(option);
    this.hasSelectionChanged = true;
    this.eventDispatcher.dispatchEvent('inputChange', {
      combobox: this,
      value: this.domRefs.inputRef.value,
    });
  }

  getSelected(): SelectOption {
    return this.listbox.getSelected();
  }

  getInputValue(): string {
    return this.domRefs.inputRef.value;
  }

  setInputValue(text: string): void {
    this.domRefs.inputRef.value = text;
  }

  open(): void {
    this.eventDispatcher.dispatchEvent('beforeOpen', { combobox: this });
    this.isOpen = true;
    if (document.activeElement !== this.domRefs.inputRef) {
      this.domRefs.inputRef.focus();
    }
    this.inputTextOnOpen = this.domRefs.inputRef.value;
    this.hasSelectionChanged = false;
    this.comboboxRef.setAttribute('aria-expanded', 'true');
    if (this.isOpen) {
      this.matchListboxVisibility();
    }
    this.eventDispatcher.dispatchEvent('afterOpen', { combobox: this });
  }

  close(): void {
    this.eventDispatcher.dispatchEvent('beforeClose', { combobox: this });
    this.isOpen = false;
    if (!this.hasSelectionChanged && !this.keepUserInput) {
      this.domRefs.inputRef.value = this.inputTextOnOpen;
      this.eventDispatcher.dispatchEvent('inputChange', {
        combobox: this,
        value: this.domRefs.inputRef.value,
      });
    }
    this.comboboxRef.classList.remove(OptionClasses.ShownClass);
    this.comboboxRef.setAttribute('aria-expanded', 'false');
    this.domRefs.inputRef.setAttribute('aria-activedescendant', '');
    this.eventDispatcher.dispatchEvent('afterClose', { combobox: this });
  }

  updateOptions(options: SelectOption[]): void {
    this.listbox.setOptions(options);
    if (this.isOpen) {
      this.matchListboxVisibility();
    }
  }

  isTouched(): boolean {
    return this.touched;
  }

  enable(): void {
    if (this.domRefs.inputRef) {
      this.domRefs.inputRef.removeAttribute('disabled');
      this.domRefs.inputRef.removeAttribute('aria-disabled');
    }
    if (this.domRefs.dropdownRef) {
      this.domRefs.dropdownRef.removeAttribute('aria-disabled');
    }
    this.comboboxRef.classList.remove(OptionClasses.IsDisabled);
    this.eventDispatcher.dispatchEvent('enabledChange', {
      combobox: this,
      enabled: false,
    });
  }

  disable(): void {
    if (this.domRefs.inputRef) {
      this.domRefs.inputRef.setAttribute('disabled', 'true');
      this.domRefs.inputRef.setAttribute('aria-disabled', 'true');
    }
    if (this.domRefs.dropdownRef) {
      this.domRefs.dropdownRef.setAttribute('aria-disabled', 'true');
    }
    this.comboboxRef.classList.add(OptionClasses.IsDisabled);
    this.eventDispatcher.dispatchEvent('enabledChange', {
      combobox: this,
      enabled: false,
    });
  }

  destroy(): void {
    super.destroy();
    this.listbox.destroy();
  }

  handleClickOutside(clickTarget: Element): void {
    if (!this.isOpen || document.activeElement === this.domRefs.inputRef) {
      return;
    }
    const isClickInside = Object.values(this.domRefs).find(
      (ref) => ref && ref.contains(clickTarget as Node)
    );
    if (!isClickInside) {
      this.close();
    }
  }

  private handleUserInput(event: InputEvent) {
    const value = this.domRefs.inputRef.value;
    if (!this.isOpen && value.length > 0) {
      this.open();
      event.preventDefault();
    }
    this.eventDispatcher.dispatchEvent('search', {
      value,
      combobox: this,
    });
    this.eventDispatcher.dispatchEvent('inputChange', {
      value,
      combobox: this,
    });
  }

  private handleKey(event: KeyboardEvent): void {
    if (!this.isOpen && (event.key === 'Down' || event.key === 'ArrowDown')) {
      event.preventDefault();
      this.open();
      return;
    }

    switch (event.key) {
      case 'Up':
      case 'ArrowUp':
      case 'Down':
      case 'ArrowDown':
      case 'Enter':
        if (this.isOpen) {
          this.listbox.handleKey(event);
        }
        return;
      case 'Esc':
      case 'Escape':
      case 'Tab':
        this.close();
        return;
    }
  }

  private ensureOptionInView(optionElement: HTMLElement) {
    const dropdownRef = this.domRefs.dropdownRef as HTMLElement;
    const dropdownTop = dropdownRef.scrollTop;
    const dropdownBottom = dropdownTop + dropdownRef.clientHeight;

    const optionTop = optionElement.offsetTop;
    const optionBottom = optionTop + optionElement.clientHeight;

    if (optionTop < dropdownTop) {
      dropdownRef.scrollTop -= dropdownTop - optionTop;
    } else if (optionBottom > dropdownBottom) {
      dropdownRef.scrollTop += optionBottom - dropdownBottom;
    }
  }

  private matchListboxVisibility() {
    if (this.listbox.isDisplayable()) {
      this.comboboxRef.classList.add(OptionClasses.ShownClass);
    } else {
      this.comboboxRef.classList.remove(OptionClasses.ShownClass);
    }
  }

  private attachListeners() {
    this.listbox.addEventListener('active', (event) => {
      if (event.element) {
        this.domRefs.inputRef.setAttribute(
          'aria-activedescendant',
          event.element.id
        );
        this.ensureOptionInView(event.element as HTMLElement);
      } else {
        this.domRefs.inputRef.setAttribute('aria-activedescendant', '');
      }
    });
    this.listbox.addEventListener('selection', (opt) => {
      this.close();
      this.eventDispatcher.dispatchEvent('selected', {
        combobox: this,
        selected: opt,
      });
    });
    this.eventDisposers.push(
      addDisposableEventListener(
        this.comboboxRef,
        'keydown',
        (event: KeyboardEvent) => this.handleKey(event)
      )
    );
    if (this.domRefs.togglerRef) {
      this.eventDisposers.push(
        addDisposableEventListener(
          this.domRefs.togglerRef,
          'click',
          (event) => {
            if (this.isOpen) {
              this.close();
            } else {
              this.open();
              event.preventDefault();
            }
          }
        )
      );
    }

    this.eventDisposers.push(
      addDisposableEventListener(this.domRefs.inputRef, 'focus', () => {
        if (!this.isOpen) {
          this.open();
        }
      })
    );
    this.eventDisposers.push(
      addDisposableEventListener(
        this.domRefs.inputRef,
        'input',
        (event: InputEvent) => this.handleUserInput(event)
      )
    );
    this.eventDisposers.push(
      addDisposableEventListener(this.domRefs.inputRef, 'blur', () => {
        this.touched = true;
        this.eventDispatcher.dispatchEvent('touched', {
          combobox: this,
        });
      })
    );
    if (this.shouldCloseOnClickOutside) {
      this.eventDisposers.push(
        addDisposableEventListener(document, 'click', (event: MouseEvent) => {
          this.handleClickOutside(event.target as Element);
        })
      );
    }
  }
}
